'use client';

import { ensurePostHogInit } from '@/shared/lib/analytics/posthog-init';
import { usePathname } from 'next/navigation';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { Suspense, useEffect, useRef } from 'react';

const IS_DEV = process.env.NODE_ENV === 'development';

// Non-UTM campaign params to capture. Add new ones here as needed.
// Aliases (affiliate/aff/ref_id/r, creator_id/cid/c) mirror what the LP forwards
// via the 308 redirect from taboo.tv/[handler] → app.taboo.tv/[handler].
const CAMPAIGN_PARAMS = [
  'ref',
  'affiliate',
  'aff',
  'ref_id',
  'r',
  'creator',
  'creator_id',
  'cid',
  'c',
  'gclid',
  'fbclid',
  'ttclid',
  'gad_source',
];

function PostHogPageViewInner() {
  const pathname = usePathname();
  const hasCapturedCampaign = useRef(false);

  // Reads the query string from `window.location.search` at effect time instead
  // of `useSearchParams()`. Under cacheComponents the hook makes this boundary a
  // permanent dynamic hole in every static shell; when the client-side resume is
  // aborted (flaky mobile network, backgrounded tab) React throws recoverable
  // error #419 for the abandoned hole. The params are only needed for a
  // side-effect, so the effect-time read closes that error class by construction
  // (same rationale as already-signed-in-toast.tsx). Tradeoff: query-only soft
  // navigations (same pathname, new params) no longer fire $pageview — filter
  // and tab changes are tracked by their own explicit events.
  useEffect(() => {
    if (!pathname) return;
    ensurePostHogInit();

    const search = window.location.search;
    const url = window.origin + pathname + search;

    posthog.capture('$pageview', { $current_url: url });

    // On first load only: register campaign params as super properties
    if (!hasCapturedCampaign.current) {
      hasCapturedCampaign.current = true;
      const params = new URLSearchParams(search);
      const campaignProps: Record<string, string> = {};

      // Dynamically capture ALL utm_* params (no hardcoded list)
      params.forEach((value, key) => {
        if (key.startsWith('utm_')) {
          campaignProps[key] = value;
        }
      });

      // Capture additional known campaign params
      for (const key of CAMPAIGN_PARAMS) {
        const value = params.get(key);
        if (value) campaignProps[key] = value;
      }

      if (Object.keys(campaignProps).length > 0) {
        posthog.register(campaignProps);
      }
    }
  }, [pathname]);

  return null;
}

function PostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageViewInner />
    </Suspense>
  );
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY || IS_DEV) return <>{children}</>;

  return (
    <PHProvider client={posthog}>
      <PostHogPageView />
      {children}
    </PHProvider>
  );
}
